/* eslint-disable @typescript-eslint/naming-convention */
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { competitor } from 'features/Competitor/store';
import { managerBooking } from 'features/ManagerBooking/store';
import { owner } from 'features/Owner/store';
import { provider } from 'features/Provider/store';
import { rental } from 'features/Rental/store';
import { rentalAgency } from 'features/RentalAgency/store';
import { unavailability } from 'features/Unavailability/store';
import storage from 'localforage';
import { createMigrate, FLUSH, PAUSE, PERSIST, persistReducer, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import { furnishing } from 'store/furnishing/slice';
import migrations from 'store/migrations';
import { accounting } from 'features/Accounting/store';
import { bed } from 'features/Bed/store';
import { booking } from './booking/slice';
import { cancelReason } from 'features/CancelReason/store';
import { creditNote } from 'features/CreditNote/store';
import { customer } from './customer/slice';
import { enquiry } from './enquiry/slice';
import { feature } from './feature/slice';
import { highlight } from './highlight/slice';
import { layout } from './layout/slice';
import { locale } from './locale/slice';
import { location } from './location/slice';
import { manager } from './manager/slice';
import { origin } from './origin/slice';
import { partner } from './partner/slice';
import { paymentMode } from './paymentMode/slice';
import { poi } from './poi/slice';
import { request } from './request/slice';
import { requestContact } from './requestContact/slice';
import { roomType } from './roomType/slice';
import { season } from './season/slice';
import { team } from './team/slice';
import { user } from './user/slice';

const serializableCheck = {
    serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    },
    immutableCheck: { warnAfter: 100 },
};

export const resourceMapping = {
    accounting: 'accounting',
    bookings: 'booking',
    competitors: 'competitor',
    customers: 'customer',
    managers: 'manager',
    owners: 'owner',
    partners: 'partner',
    providers: 'provider',
    seasons: 'season',
    users: 'user',
    beds: 'bed',
    highlights: 'highlight',
    locations: 'location',
    managerBookings: 'managerBooking',
    point_of_interests: 'poi',
    rentals: 'rental',
    room_types: 'roomType',
    furnishings: 'furnishing',
    payment_modes: 'paymentMode',
    enquiries: 'enquiry',
    origins: 'origin',
    creditNotes: 'creditNote',
    requestContact: 'requestContact',
    cancelReason: 'cancelReason',
    unavailability: 'unavailability',
};

export const sharedResources = [
    'beds',
    'features',
    'furnishings',
    'locations',
    'point_of_interests',
    'rental_agencies',
    'room_types',
];

const reducers = {
    accounting,
    bed,
    booking,
    competitor,
    customer,
    feature,
    highlight,
    furnishing,
    layout,
    location,
    locale,
    manager,
    managerBooking,
    owner,
    partner,
    paymentMode,
    poi,
    provider,
    rental,
    rentalAgency,
    request,
    roomType,
    season,
    team,
    user,
    enquiry,
    origin,
    creditNote,
    requestContact,
    cancelReason,
    unavailability,
};

const persistConfig = {
    key: 'root',
    version: 16,
    storage,
    migrate: createMigrate(migrations, { debug: true }),
};

export const combinedReducers = combineReducers(reducers);

const persistedReducer = persistReducer(persistConfig, combinedReducers);

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware(serializableCheck),
    devTools: process.env.NODE_ENV !== 'production',
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
